import { useState, type DragEvent } from "react";
import type { GameVersion } from "@redchain/contracts";
import { acceptRomFile, versionLabel } from "../main/RomLoader";
import type { RomRecord } from "../main/db";

/** File picker + drag-and-drop for the player's own ROM (WEB-2). */
export function RomPicker({ lockedVersion, onRom }: { lockedVersion: GameVersion | null; onRom: (rom: RomRecord) => void }) {
  const [error, setError] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const [over, setOver] = useState(false);

  const handle = async (file: File | undefined) => {
    if (!file) return;
    setChecking(true);
    setError(null);
    try {
      const r = await acceptRomFile(file, lockedVersion);
      if (r.ok) onRom(r.rom);
      else if (r.reason === "unknown_rom")
        setError("This isn't a supported ROM. RedChain needs an unmodified English Pokémon Red or Pokémon Blue ROM.");
      else
        setError(
          `That's ${versionLabel(r.detected)}, but your current game is ${versionLabel(r.required)}. Load your ${versionLabel(r.required)} ROM to keep playing it, or start a new game from your profile.`,
        );
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setChecking(false);
    }
  };

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setOver(false);
    void handle(e.dataTransfer.files[0]);
  };

  return (
    <div
      className={`dropzone ${over ? "over" : ""}`}
      onDragOver={(e) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={onDrop}
    >
      <p>
        {lockedVersion ? `Load your ${versionLabel(lockedVersion)} ROM.` : "Load your Pokémon Red or Pokémon Blue ROM."} It stays on this device and is
        never uploaded.
      </p>
      <label className="button">
        Choose ROM file
        <input type="file" accept=".gb,.gbc,.bin,application/octet-stream" hidden onChange={(e) => void handle(e.target.files?.[0])} />
      </label>
      <p className="hint">…or drop the file here.</p>
      {checking && <p className="status">Checking…</p>}
      {error && <p className="status error">{error}</p>}
    </div>
  );
}
